// AIUsage.jsx — Phase 1 Billing Infrastructure
// Per-agent AI usage dashboard: spend by service/feature, wallet, events.

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Zap, DollarSign, TrendingUp, Clock, BarChart3, CreditCard,
  RefreshCw, Settings, AlertCircle, CheckCircle, ArrowUpRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPut, apiPost } from '../lib/api.js';

const SERVICE_LABELS = {
  openai_chat:      'OpenAI Chat',
  openai_whisper:   'OpenAI Whisper (STT)',
  openai_tts:       'OpenAI TTS',
  elevenlabs_tts:   'ElevenLabs TTS',
  elevenlabs_clone: 'ElevenLabs Clone',
  perplexity_sonar: 'Perplexity Sonar',
  twilio_voice:     'Twilio Voice',
  twilio_sms:       'Twilio SMS',
  sendgrid_email:   'SendGrid Email',
};

const FEATURE_LABELS = {
  roleplay:          'AI Roleplay',
  underwriting_bot:  'Underwriting Bot',
  ai_dialer:         'AI Dialer',
  lead_summary:      'Lead Summary',
  drip_writer:       'Drip Writer',
  civix_assistant:   'CIVIX Assistant',
  compliance:        'Compliance',
  other:             'Other',
};

const SERVICE_COLORS = [
  'bg-civix-purple/30',
  'bg-blue-500/30',
  'bg-cyan-500/30',
  'bg-green-500/30',
  'bg-yellow-500/30',
  'bg-orange-500/30',
  'bg-red-500/30',
  'bg-pink-500/30',
  'bg-indigo-500/30',
];

function fmt(v) {
  if (v == null || isNaN(v)) return '$0.00';
  if (v < 0.01 && v > 0) return `<$0.01`;
  return `$${v.toFixed(2)}`;
}

const TOPUP_TIERS = [25, 50, 100, 250, 500];

export default function AIUsage() {
  const [showCapModal, setShowCapModal] = useState(false);
  const [newCap, setNewCap] = useState('');

  const { data: rawData, isLoading, refetch } = useQuery({
    queryKey: ['ai-usage-me'],
    queryFn: () => apiGet('/ai-usage/me'),
    staleTime: 30000,
  });

  const data = rawData?.data || rawData || {};
  const { daily = 0, weekly = 0, monthly = 0, wallet = {}, byService = [], byFeature = [], recentEvents = [], cap = {} } = data;

  const setCapMutation = useMutation({
    mutationFn: ({ monthlyCapUsd }) => apiPut('/ai-usage/wallet/cap', { monthlyCapUsd }),
    onSuccess: () => {
      toast.success('Monthly cap updated');
      setShowCapModal(false);
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to update cap'),
  });

  const topupMutation = useMutation({
    mutationFn: ({ tierId }) => apiPost('/stripe/topup', { tierId }),
    onSuccess: (resp) => {
      const d = resp?.data || resp;
      if (d.mode === 'dev') {
        toast.success(d.message || 'Wallet credited (dev mode)');
        refetch();
      } else if (d.checkoutUrl) {
        window.location.href = d.checkoutUrl;
      }
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Top-up failed'),
  });

  const maxServiceCost = Math.max(...byService.map((s) => s.totalCost), 0.001);
  const maxFeatureCost = Math.max(...byFeature.map((f) => f.totalCost), 0.001);

  if (isLoading) {
    return (
      <div className="p-8 text-white/40 flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
        Loading usage data…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Billing</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
            <Zap className="w-7 h-7 text-civix-purpleSoft" />
            AI Usage
          </h1>
          <p className="text-white/50 text-sm mt-1">Your AI spend, broken down by service and feature.</p>
        </div>
        <button onClick={() => refetch()} className="btn-ghost text-sm flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </header>

      {/* Spend Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SpendCard label="Today" value={daily} icon={Clock} />
        <SpendCard label="This Week" value={weekly} icon={TrendingUp} />
        <SpendCard label="This Month" value={monthly} icon={BarChart3} />
        <WalletCard wallet={wallet} cap={cap} onTopUp={(amt) => topupMutation.mutate({ tierId: `topup_${amt}` })} />
      </div>

      {/* Cap Alert */}
      {cap.capUsd > 0 && (
        <div className={`panel p-4 flex items-center gap-3 border ${cap.allowed ? 'border-green-400/20' : 'border-red-400/30'}`}>
          {cap.allowed ? (
            <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          )}
          <div className="flex-1">
            <p className="text-sm">
              <span className="font-medium">Monthly cap: </span>
              {fmt(cap.currentUsd)} of {fmt(cap.capUsd)} used
              {!cap.allowed && <span className="text-red-400 ml-2">— Cap reached, AI features may be restricted</span>}
            </p>
            {cap.allowed && (
              <div className="mt-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-civix-purple rounded-full transition-all"
                  style={{ width: `${Math.min(100, (cap.currentUsd / cap.capUsd) * 100)}%` }}
                />
              </div>
            )}
          </div>
          <button onClick={() => { setNewCap(String(cap.capUsd)); setShowCapModal(true); }} className="btn-ghost text-xs">
            <Settings className="w-3.5 h-3.5 mr-1" /> Edit Cap
          </button>
        </div>
      )}

      {!cap.capUsd && (
        <div className="panel p-4 flex items-center justify-between gap-3 border border-civix-border/30">
          <p className="text-sm text-white/60">No monthly cap set — AI usage is unlimited.</p>
          <button onClick={() => { setNewCap(''); setShowCapModal(true); }} className="btn-ghost text-xs">
            <Settings className="w-3.5 h-3.5 mr-1" /> Set Cap
          </button>
        </div>
      )}

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownCard
          title="By Service"
          rows={byService}
          labelKey="service"
          labelMap={SERVICE_LABELS}
          maxCost={maxServiceCost}
          colors={SERVICE_COLORS}
        />
        <BreakdownCard
          title="By Feature"
          rows={byFeature}
          labelKey="feature"
          labelMap={FEATURE_LABELS}
          maxCost={maxFeatureCost}
          colors={SERVICE_COLORS}
        />
      </div>

      {/* Recent Events */}
      <div className="panel overflow-hidden">
        <div className="p-4 border-b border-civix-border/50 flex items-center justify-between">
          <h3 className="font-semibold">Recent Usage Events</h3>
          <span className="text-xs text-white/40">{recentEvents.length} events</span>
        </div>
        {recentEvents.length === 0 ? (
          <div className="p-8 text-center text-white/30 text-sm">No usage events yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-civix-border/30">
                  <th className="text-left p-3 text-white/40 font-medium">Time</th>
                  <th className="text-left p-3 text-white/40 font-medium">Service</th>
                  <th className="text-left p-3 text-white/40 font-medium">Feature</th>
                  <th className="text-right p-3 text-white/40 font-medium">Units</th>
                  <th className="text-right p-3 text-white/40 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-civix-border/20">
                {recentEvents.slice(0, 50).map((ev) => (
                  <tr key={ev._id} className="hover:bg-civix-panelLight/30 transition">
                    <td className="p-3 text-white/40 text-xs">{new Date(ev.createdAt).toLocaleString()}</td>
                    <td className="p-3 text-white/80">{SERVICE_LABELS[ev.service] || ev.service}</td>
                    <td className="p-3">
                      <span className="text-xs px-2 py-0.5 bg-civix-purple/10 text-civix-purpleSoft/80 rounded-full">
                        {FEATURE_LABELS[ev.feature] || ev.feature}
                      </span>
                    </td>
                    <td className="p-3 text-right text-white/50 text-xs">{ev.units?.toLocaleString()} {ev.unitType}</td>
                    <td className="p-3 text-right font-mono text-xs text-green-400">{fmt(ev.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monthly Cap Modal */}
      {showCapModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="panel max-w-sm w-full p-6">
            <h3 className="font-semibold mb-4">Set Monthly Cap</h3>
            <p className="text-sm text-white/60 mb-4">
              Set a maximum monthly AI spend. When reached, AI features will be restricted. Set to 0 for unlimited.
            </p>
            <div className="flex gap-2 mb-2">
              <span className="flex items-center text-white/50 pl-3 border border-civix-border/50 rounded-l-lg">$</span>
              <input
                type="number"
                value={newCap}
                onChange={(e) => setNewCap(e.target.value)}
                placeholder="0 = unlimited"
                className="flex-1 bg-civix-panelLight border border-civix-border/50 rounded-r-lg px-3 py-2 text-sm text-white focus:outline-hidden focus:border-civix-purple/50"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setCapMutation.mutate({ monthlyCapUsd: Number(newCap) || 0 })}
                disabled={setCapMutation.isPending}
                className="btn-primary flex-1"
              >
                {setCapMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setShowCapModal(false)} className="btn-ghost flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SpendCard({ label, value, icon: Icon }) {
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-civix-purpleSoft/70" />
        <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold font-mono">{fmt(value)}</p>
    </div>
  );
}

function WalletCard({ wallet, cap, onTopUp }) {
  const [showTopUp, setShowTopUp] = useState(false);
  return (
    <div className="panel p-5 relative">
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-4 h-4 text-civix-purpleSoft/70" />
        <span className="text-xs text-white/50 uppercase tracking-wider">Wallet Balance</span>
      </div>
      <p className="text-2xl font-bold font-mono">{fmt(wallet.balanceUsd)}</p>
      <button
        onClick={() => setShowTopUp(!showTopUp)}
        className="mt-2 text-xs text-civix-purpleSoft/80 hover:text-civix-purpleSoft flex items-center gap-1"
      >
        <ArrowUpRight className="w-3 h-3" /> Top Up
      </button>
      {showTopUp && (
        <div className="absolute left-0 right-0 top-full mt-1 z-10 panel p-3 shadow-xl">
          <p className="text-xs text-white/50 mb-2">Select amount:</p>
          <div className="grid grid-cols-5 gap-1">
            {TOPUP_TIERS.map((amt) => (
              <button
                key={amt}
                onClick={() => { onTopUp(amt); setShowTopUp(false); }}
                className="px-2 py-1.5 rounded-sm bg-civix-panelLight hover:bg-civix-purple/20 text-xs font-medium text-white/80 hover:text-white transition"
              >
                ${amt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BreakdownCard({ title, rows, labelKey, labelMap, maxCost, colors }) {
  return (
    <div className="panel p-5">
      <h3 className="font-semibold mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-white/30">No data yet</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row, i) => {
            const pct = (row.totalCost / maxCost) * 100;
            const label = labelMap[row[labelKey]] || row[labelKey];
            return (
              <div key={row[labelKey]}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white/80">{label}</span>
                  <span className="text-sm font-mono font-medium">{fmt(row.totalCost)}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${colors[i % colors.length]}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-xs text-white/30 mt-0.5">{row.count} events</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
